import React, { PropsWithChildren } from "react";
import {
  IInsightStory,
  StoryContextProps,
} from "@widgets/components/__Insights/InsightStoryList/types/InsightStoryTypes";
import { saEvent, useToken } from "@iliotech/watchlist-api";
import { useAllMarketCategoryRanking } from "@iliotech/watchlist-api/src/hooks/useAllMarketCategoryRanking";
import { useSortCategory } from "@iliotech/watchlist-api/src/hooks/useSortCategory";
import { useSortContext } from "@iliotech/watchlist-api/src/context/SortContext";
import { MarketData } from "@iliotech/watchlist-api/src/types/MarketData";
import { extractNumericValue } from "@widgets/utils";
import { ScatterItem } from "@widgets/main";

const Y_AXIS_DICT: { [insightId: string]: keyof MarketData } = {
  PERFORMANCE_VS_MARKET: "Largest Market Out and Under Performers - 1 year",
  VOLATILITY_BANDS_MARKET: "Volatility (Annualized) - 1 year",
  LARGEST_VOL_MOVE: "Volatility Change (Percentage) - 1 year",
  BETA_BANDS: "Market Impact (Beta) - 1 year",
};

const PERCENTAGE_AXIS_ITEMS_KEYWORDS = [
   "volatility",
  "dividend yield",
  "price return",
] ;

const INITIAL_X_AXIS = "Price Return - 1 Year";

export const X_AXIS_ITEMS = [
  INITIAL_X_AXIS,
  "Volatility Change (Percentage) - 1 year",
  "Market Impact (Beta) - 1 year",
  "Volatility (Annualized) - 1 year",
  "Dividend Yield"
] as const satisfies readonly (keyof MarketData)[];

export const StoryContext =
  React.createContext<ReturnType<typeof useStoryContextValue> | null>(null);

export const StoryProvider = <Type, Settings>({
  children,
  story,
  settings,
  loader,
}: PropsWithChildren<StoryContextProps<Type, Settings>>) => {
  const value = useStoryContextValue(story, settings as any, loader);

  return (
    <StoryContext.Provider value={value}>{children}</StoryContext.Provider>
  );
};

const useStoryContextValue = <Type, Settings, ResponseType>(
  story: IInsightStory<Type>,
  settings: Settings,
  loader: (
    insightId: string,
    settings: Settings
  ) => Promise<{ data: ResponseType }>
) => {
  const [scatterPoints, setScatterPoints] = React.useState<ScatterItem[]>([]);
  const { environment } = useToken();
  const { watchlistId } = useSortContext();
  const { data: sortCategories } = useSortCategory(watchlistId, environment);
  const { data } = useAllMarketCategoryRanking({
    watchlistId: watchlistId,
    environment,
    sortCategories: sortCategories || [],
    overrideAllSort: true,
  });
  const [currentInsightId, setCurrentInsightId] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [currentInsight, setCurrentInsight] =
    React.useState<{ Component: any; data: ResponseType }>();
  const inFlightRequest = React.useRef("");
  const [loadedInsights, setLoadedInsights] = React.useState<{
    [insightId: string]: { Component: any; data: ResponseType } | undefined;
  }>({});
  const [xAxis, setXAxis] = React.useState<keyof MarketData>(INITIAL_X_AXIS);
  const yAxis = Y_AXIS_DICT[currentInsightId];
  
  const yAxisFormatter = React.useMemo(() => {
    let formatter = (value: number) => `${value.toFixed(0)}%`;
    const field = yAxis;
    const sampleValue = data?.find(item => 
      item[field] !== null && 
      item[field] !== undefined && 
      item[field] !== 'N/A'
    )?.[field];
    const lastChar = sampleValue?.toString().slice(-1);
     if (lastChar === 'x') {
      formatter = (value: number) => `${value.toFixed(2)}x`;
    }else if (lastChar === '%') {
      formatter = (value: number) => `${value.toFixed(2)}%`;
    }
    return formatter;
  }, [yAxis, data]);
  
    const xAxisFormatter = React.useMemo(() => {
    let formatter = (value: number) => `${value.toFixed(0)}%`;
    const field = xAxis;
    
    const sampleValue = data?.find(item => 
      item[field] !== null && 
      item[field] !== undefined && 
      item[field] !== 'N/A'
    )?.[field];
   console.log({x:sampleValue})
    const lastChar = sampleValue?.toString().slice(-1);
    if (lastChar === 'x') {
      formatter = (value: number) => `${value.toFixed(2)}x`;
    }else if (lastChar === '%') {
      formatter = (value: number) => `${value.toFixed(2)}%`;
    }
    return formatter;
  }, [xAxis, data]);

  React.useEffect(() => {
    if (!story.insights.length) {
      return;
    }
    const firstInsight = story.insights[0];
    loadInsight(firstInsight.id);
  }, [story, settings]);

  const loadInsight = (insightId: string) => {
    console.log("loadInsight", insightId);
    const insight = story.insights.find((i) => i.id === insightId);
    if (!insight) {
      // throw new Error(`Insight with id ${insightId} not found`);
      return;
    }
    setScatterPoints([]);
    setCurrentInsightId(insightId);
    saEvent("insight-loaded", {
      insightId,
      type: story.type,
    });
    if (loadedInsights[insightId]) {
      setCurrentInsight(loadedInsights[insightId]);
      setLoading(false);
      return;
    }

    if (inFlightRequest.current) {
      // TODO: cancel the in flight request
    }

    setLoading(true);
    loader(insightId, settings)
      .then(({ data }) => {
        const nextInsight = { data, Component: insight.Component };
        setLoadedInsights((prev) => ({ ...prev, [insightId]: nextInsight }));
        setCurrentInsight(nextInsight);
      })
      .catch(() => {
        console.error("Error loading insight");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changeXAxis = (xAxis: keyof MarketData) => {
    setXAxis(xAxis);
    const instrumentIds = scatterPoints.map((point) =>
      data?.find((d) => d.name === point.label)?.instrumentId
    ).filter((id): id is string => id !== undefined);
    const extraField = yAxis === "Largest Market Out and Under Performers - 1 year" ? "Total Return - 1 Year" : undefined;
    const extraFieldFormatter = (value: number) => `${value.toFixed(2)}%`;
    const chartData = getGlobalScatterData(xAxis, yAxis, instrumentIds, extraField,extraFieldFormatter);

    setScatterPoints(chartData);
  };

  const onSeriesItemClick = (items: string[]) => {
    console.log({xAxis, yAxis})
    if (!xAxis || !yAxis) return;
    const extraField = yAxis === "Largest Market Out and Under Performers - 1 year" ? "Total Return - 1 Year" : undefined;
    const extraFieldFormatter = (value: number) => `${value.toFixed(2)}%`;
    const universe = items
      .map((item) => data?.find((d) => d.name === item)?.instrumentId)
      .filter((item): item is string => item !== undefined);
    const chartData = getGlobalScatterData(xAxis, yAxis, universe, extraField,extraFieldFormatter);
    setScatterPoints(chartData);
  };


  const getInstrumentIdByLabel = (label: string) => {
    const instrumentId = data?.find((d) => d.name === label)?.instrumentId;
    return instrumentId;
  };

  const getGlobalScatterData = (
    xField: keyof MarketData,
    yField: keyof MarketData,
    universe: string[],
    extraField?: keyof MarketData,
    extraFieldFormatter?: (value: number) => string
  ) => {
    const filteredData = data?.filter((item) =>
      universe.includes(item.instrumentId)
    );
    const items: ScatterItem[] = (filteredData || []).map((item) => ({
      xField: extractNumericValue(item[xField]!),
      yField: extractNumericValue(item[yField]!),
      extraField: extraField 
        ? extraFieldFormatter 
          ? extraFieldFormatter(Number(extractNumericValue(item[extraField]!))) 
          : extractNumericValue(item[extraField]!) 
        : undefined,
      extraFieldLabel: extraField ? extraField : undefined,
      label: item.name,
      ratio:
        Number(extractNumericValue(item[xField]!)) /
        Number(extractNumericValue(item[yField]!)),
    }));
    return items;
  };

  const moveNext = () => {
    const index = story.insights.findIndex((i) => i.id === currentInsightId);
    setScatterPoints([]);
    let nextIndex = 0;
    if (index === -1 || index >= story.insights.length - 1) {
      nextIndex = 0;
    } else {
      nextIndex = index + 1;
    }
    const nextId = story.insights[nextIndex].id;
    loadInsight(nextId);
  };

  const movePrevious = () => {
    const index = story.insights.findIndex((i) => i.id === currentInsightId);
    setScatterPoints([]);
    let nextIndex = 0;
    if (index === -1 || index === 0) {
      nextIndex = story.insights.length - 1;
    } else {
      nextIndex = index - 1;
    }
    loadInsight(story.insights[nextIndex].id);
  };

  const selectCategory = (categoryId: string) => {
    if (!categoryId) return;
    if (currentInsightId) {
      const current = story.insights.find((i) => i.id === currentInsightId);
      if (current?.categoryId === categoryId) {
        moveNext();
        return;
      }
    }

    const insight = story.insights.find((i) => i.categoryId === categoryId);
    if (!insight) return;
    loadInsight(insight.id);
  };

  return {
    moveNext,
    movePrevious,
    selectCategory,
    currentInsightId,
    currentInsight,
    loadInsight,
    loading,
    story,
    getGlobalScatterData,
    onSeriesItemClick,
    scatterPoints: scatterPoints.filter((p) => p.xField !== undefined && p.yField !== undefined),
    setScatterPoints,
    getInstrumentIdByLabel,
    xAxis,
    setXAxis,
    yAxis,
    changeXAxis,
    xAxisFormatter,
    yAxisFormatter,
  };
};

export const useStoryContext = () => {
  const context = React.useContext(StoryContext);
  if (context === null) {
    throw new Error("useStoryContext must be used within a StoryProvider");
  }
  return context;
};
